import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { processQuestions } from "../../admin/questionSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import "../fonts/breuer-headline.css";
import "./rules.css";

import rulesDesk from "../img/rules_desk.jpg";
import rulesMob from "../img/rules_mob.jpg";
import greenBtn from "../img/green_button.png";
import icon1 from "../img/icon1.png";
import icon2 from "../img/icon2.png";
import icon3 from "../img/icon3.png";
import icon4 from "../img/icon4.png";
import icon5 from "../img/icon5.png";
import icon6 from "../img/icon6.png";

const RULE_CARDS = [
  {
    icon: icon1,
    title: "CLICK TO INTERACT",
    description: "Tap on objects and areas to explore the museum.",
  },
  {
    icon: icon2,
    title: "SOLVE PUZZLES",
    description: "Drag, drop and solve puzzles to find hidden clues.",
  },
  {
    icon: icon3,
    title: "LISTEN CAREFULLY",
    description: "Listen to audio clues and stories that reveal important hints.",
  },
  {
    icon: icon4,
    title: "FIND HIDDEN ARTIFACTS",
    description: "Search every corner to find hidden artifacts and secrets.",
  },
  {
    icon: icon5,
    title: "RESTORE ARTIFACTS",
    description: "Use clues to restore artifacts and unlock new exhibits.",
  },
  {
    icon: icon6,
    title: "COMPLETE THE MISSION",
    description: "Solve all puzzles, restore the museum and complete your mission.",
  },
];

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const [nextLoading, setNextLoading] = useState(false);

  useEffect(() => {
    dispatch(setBackButtonUrl("/intro"));
  }, [dispatch]);

  useEffect(() => {
    return setupAppPageBodyBackground(document.body, {
      deskForeground: rulesDesk,
      mobForeground: rulesMob,
    });
  }, []);

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;

    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;
    if (!needsQuizInit) return;

    dispatch(processQuestions())
      .unwrap()
      .then(() =>
        initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        })
      )
      .catch((err) => {
        console.error("Error in process→init chain:", err);
      });
  }, [themeData, user, adminToken, dispatch]);

  const handleContinue = useCallback(
    (e) => {
      e.preventDefault();
      if (!user || nextLoading) return;
      setNextLoading(true);
      navigate("/begin", { replace: true });
    },
    [user, nextLoading, navigate]
  );

  return (
    <div className="rules-page">
      <div className="rules-layout">
        <header className="rules-header">
          <h1 className="rules-title">
            <span className="rules-title__flourish" aria-hidden="true" />
            HOW TO PLAY
            <span className="rules-title__flourish" aria-hidden="true" />
          </h1>
          <p className="rules-subtitle">Follow the steps. Restore the legacy.</p>
        </header>

        <ul className="rules-grid">
          {RULE_CARDS.map((card, index) => (
            <li key={card.title} className="rules-card">
              <div className="rules-card__icon-wrap">
                <img
                  src={card.icon}
                  alt=""
                  className="rules-card__icon"
                  draggable={false}
                />
              </div>
              <div className="rules-card__content">
                <div className="rules-card__heading">
                  <span className="rules-card__num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <h2 className="rules-card__title">
                    <span className="rules-card__num-inline" aria-hidden="true">
                      {index + 1}{" "}
                    </span>
                    {card.title}
                  </h2>
                </div>
                <p className="rules-card__desc">{card.description}</p>
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="rules-continue-btn"
          disabled={nextLoading || !user}
          onClick={handleContinue}
          style={{ backgroundImage: `url(${greenBtn})` }}
        >
          {nextLoading ? "LOADING…" : "CONTINUE"}
        </button>
      </div>
    </div>
  );
}

export default UserRules;
